<template>
  <div class="m-auto max-w-screen-xxxl">
    <div
      class="mb-[75px] mt-[34px] px-16 text-center text-lg uppercase text-grey-dark-02 md:mb-[121px]"
    >
      {{ countText }}
    </div>

    <MagazineHighlightGridNews
      v-if="highlightSection"
      :articles="highlightSection.articles"
      :url="highlightSection.url"
    />

    <section
      v-for="section in otherSections"
      :key="section.uuid"
      class="mt-64 md:mt-[135px]"
    >
      <div class="px-15 md:px-72">
        <div class="border-t border-grey-light-01">
          <Text
            class="mb-[11px] mt-28 text-center text-2xl font-light uppercase md:mb-[52px] md:mt-32"
            :class="{
              '!font-bold': isRebranding,
            }"
          >
            {{ section.label }}
          </Text>
        </div>
      </div>

      <BlokkliStandaloneWrapper v-if="section.paragraph">
        <BlokkliItem
          v-bind="section.paragraph"
          class="!mb-[90px] pt-[11px] md:!mb-112 md:pt-[45px]"
        />
      </BlokkliStandaloneWrapper>

      <MagazineHighlightGridTopic
        :articles="section.articles"
        class="md:mt-[76px]"
      />

      <div class="mb-[115px] flex justify-center md:mb-0">
        <NuxtLink
          class="link-button mx-auto mt-80 inline-flex uppercase md:mt-112"
          :to="section.url"
          icon-end="arrow-right"
        >
          {{ $texts('moreStories', 'More Stories') }}
          <SpriteSymbol name="arrow-right" class="ml-4 h-[18px] w-[18px]" />
        </NuxtLink>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
/**
 * This is the "All" (overview) page of "World of Oris". It renders an
 * excerpt of all magazine categories.
 */
import type {
  MagazineArticleTeaserFragment,
  MagazineOverviewCategoryFragment,
} from '#graphql-operations'
import { falsy, narrowTypeByProperty } from '~/helpers/type'

definePageMeta({
  name: 'worldoforis',
})

const { $texts } = useNuxtApp()
const isRebranding = await useFeatureFlag()

const { data } = await useAsyncData(() =>
  useCachedGraphqlQuery('routeMagazinOverview').then((v) => v.data),
)

const metatags = buildDrupalMetatags(data.value?.node?.metatags?.metatags)
useHead(metatags)

type OverviewSection = {
  uuid: string
  label: string
  url: string
  articles: MagazineArticleTeaserFragment[]
  paragraph: MagazineOverviewCategoryFragment['paragraph']
}

const sections = computed<OverviewSection[]>(() =>
  (data.value?.categories.items || [])
    .map((v) => narrowTypeByProperty(v, 'articles'))
    .filter(falsy)
    .map((v) => {
      const articles = (v.articles?.query.items || [])
        .map((a) => narrowTypeByProperty(a, 'title'))
        .filter(falsy)
      if (v.label && v.url?.path && articles.length) {
        return {
          uuid: v.uuid,
          label: v.label,
          url: v.url.path,
          articles,
          paragraph: v.paragraph,
        }
      }

      return null
    })
    .filter(falsy),
)

const countText = computed(() => {
  const total = data.value?.totalArticlesQuery.total || 0
  return (
    total === 1
      ? $texts('storySingular', '1 Story')
      : $texts('storyPlural', '@count Stories')
  ).replace('@count', total.toString())
})

const highlightSection = computed(() => sections.value[0])
const otherSections = computed(() => sections.value.slice(1))
</script>

<style lang="scss" scoped>
:deep(.paragraph),
.paragraph {
  @apply mb-[83px] mt-[31px] md:mb-[126px] md:mt-0;
}
</style>
